import type { Logger } from "@expert/logging";
import type {
    GaiaExpertAssistAppName,
    OrchestratorInvokeMessagePayload,
    Partner,
    TranscriptSnippet,
} from "@expert/shared-types";

import { type GaiaWebSocketResponse, type GaiaWebsocketContext, gaiaWsEventBus } from "@expert/gaia";

import type { DeadAirClassification } from "./deadAirTypes";

import { DEAD_AIR_REQUEST_TIMEOUT_MS } from "./deadAirConfig";
import { registerDeadAirCorrelationId, unregisterDeadAirCorrelationId } from "./deadAirCorrelationRegistry";
import { buildDeadAirPrompt } from "./deadAirPrompt";
import { parseDeadAirResponse } from "./parseDeadAirResponse";

export interface DeadAirRequestContext {
    appName: GaiaExpertAssistAppName;
    partner: Partner;
    /** Timeline session id (used as `sessionGroupId`). */
    sessionId: string;
    /** Active voice call id (used as GAIA `sessionId`, mirroring the voice path in useSendExpertAssistMessage). */
    callSid: string;
    expertId: string;
    lineOfBusiness?: string;
}

export interface RequestDeadAirClassificationArgs {
    transcript: TranscriptSnippet[];
    gapSeconds: number;
    context: DeadAirRequestContext;
    sendJsonMessage: GaiaWebsocketContext["sendJsonMessage"];
    logger: Logger;
    signal?: AbortSignal;
    timeoutMs?: number;
}

/**
 * Silently asks Expert Assist (GAIA orchestrator) to classify why the call has gone quiet and, when it
 * is confident the agent is looking up something it can answer, to return that answer.
 *
 * Mirrors the out-of-band request/response pattern of gaiaSpellCheck: it builds an `orchestrator-invoke`
 * directly (so NO expert bubble is ever added to the timeline), awaits the reply matched by
 * correlationId, and renders nothing itself. The correlationId is registered while the request is
 * in-flight and, once it settles — including via timeout or abort, after the WebSocket message has
 * already been sent and can no longer be recalled — it is tombstoned for a bounded TTL, so a reply that
 * arrives late still matches the registry and stays invisible to the expert.
 *
 * Resolves to a {@link DeadAirClassification}, or `null` on timeout / orchestrator error / abort /
 * unparseable reply. The caller decides whether the classification warrants surfacing a suggestion.
 */
export const requestDeadAirClassification = async ({
    transcript,
    gapSeconds,
    context,
    sendJsonMessage,
    logger,
    signal,
    timeoutMs = DEAD_AIR_REQUEST_TIMEOUT_MS,
}: RequestDeadAirClassificationArgs): Promise<DeadAirClassification | null> => {
    const correlationId = crypto.randomUUID();
    registerDeadAirCorrelationId(correlationId);

    try {
        return await new Promise<DeadAirClassification | null>((resolve) => {
            let settled = false;

            const finish = (result: DeadAirClassification | null) => {
                if (settled) return;
                settled = true;
                unsubSuccess();
                unsubError();
                clearTimeout(timer);
                signal?.removeEventListener("abort", onAbort);
                resolve(result);
            };

            const unsubSuccess = gaiaWsEventBus.on(
                "gaia_ws_orchestrator-invoke-success",
                (response: GaiaWebSocketResponse) => {
                    if (response.correlationId !== correlationId) return;
                    finish(parseDeadAirResponse(response.body, logger));
                },
            );

            const unsubError = gaiaWsEventBus.on(
                "gaia_ws_orchestrator-invoke-error",
                (response: GaiaWebSocketResponse) => {
                    if (response.correlationId !== correlationId) return;
                    // Only the status fields: the error body also echoes the request, transcript included.
                    const { status, statusText, trackingId, responseText } = (response.body ?? {}) as Record<
                        string,
                        unknown
                    >;
                    logger.warn(
                        { correlationId, statusCode: status, statusText, trackingId, responseText },
                        "Dead-air classification orchestrator error",
                    );
                    finish(null);
                },
            );

            const timer = setTimeout(() => {
                logger.warn({ correlationId, timeoutMs }, "Dead-air classification timed out");
                finish(null);
            }, timeoutMs);

            const onAbort = () => finish(null);
            if (signal) {
                if (signal.aborted) {
                    finish(null);
                    return;
                }
                signal.addEventListener("abort", onAbort, { once: true });
            }

            const payload: OrchestratorInvokeMessagePayload = {
                action: "orchestrator-invoke",
                appName: context.appName,
                correlationId,
                message: buildDeadAirPrompt({ transcript, gapSeconds }),
                messageId: crypto.randomUUID(),
                messageType: "Search",
                partner: context.partner,
                sessionId: context.callSid,
                sessionGroupId: context.sessionId,
                userId: context.expertId,
                userType: "expert",
                // Intentionally omit experimentName: the dead-air ask must be a plain search, not routed
                // through sales-faq / grounded-search experiment variants.
                lineOfBusiness: context.lineOfBusiness,
            };

            sendJsonMessage(payload);
            // Record the borrowed GAIA session alongside the ask, so a later degraded expert response
            // can be correlated back to a preceding dead-air classification (README risk #2,
            // conversation-memory bleed). That linkage already exists in the payload via
            // sessionGroupId; what was missing was any way to grep it in production.
            logger.info(
                { correlationId, sessionGroupId: context.sessionId, callSid: context.callSid },
                "Dead-air classification ask sent",
            );
        });
    } finally {
        unregisterDeadAirCorrelationId(correlationId);
    }
};
